import React from "react";
import { graphql } from "gatsby";
import ArticleWrapper from "../components/Article/Wrapper/index.js";
import ArticleTitle from "../components/Article/ArticleTitle/index.js";
import Breadcrumbs from "../components/Breadcrumbs/index.js";
import Patterns from "../container/Patterns.js";
import FastLinks from "../components/FastLinks/index.js";
import AccountList from "../components/AccountList/index.js";
import ServiceList from "../components/ServiceList/index.js";
import PlainCards from "../components/PlainCards/index.js";
import FeatureCards from "../components/FeatureCards/index.js";
import ScrollSlider from "../components/ScrollSlider/index.js";
import data from "../components/SmallHero/data.js";
import { buildURLForMenuItem } from "../utils/buildURL.js";
import { Helmet } from "react-helmet";
import { text } from "../container/language.js";
import { usePageContext } from "../utils/PageContext.js";

const byDisplayOrder = (items) =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const orderA = a.item.displayOrder;
      const orderB = b.item.displayOrder;

      if (orderA == null && orderB == null) return a.index - b.index;
      if (orderA == null) return 1;
      if (orderB == null) return -1;
      if (orderA !== orderB) return orderA - orderB;

      return a.index - b.index;
    })
    .map(({ item }) => item);

export const CategoryItems = ({
  items,
  articleListType,
  body,
  parentCategory,
  blockMode = false,
}) => {
  return (
    <>
      {articleListType === "SCROLLING_SLIDER" && (
        <ScrollSlider
          data={items}
          blockMode={blockMode}
          parentCategory={parentCategory}
        />
      )}
      {articleListType === "PLAIN_CARD" && (
        <section class="site-block">
          <div class="wrapper">
            <PlainCards
              data={items}
              blockMode={blockMode}
              parentCategory={parentCategory}
            />
          </div>
        </section>
      )}
      {articleListType === "CARD_WITH_IMAGE" && (
        <section class="site-block">
          <div class="wrapper">
            <FeatureCards
              data={items}
              blockMode={blockMode}
              parentCategory={parentCategory}
            />
          </div>
        </section>
      )}
      {articleListType === "TILTING_PLAIN_CARD" && (
        <section class="site-block">
          <div class="wrapper">
            <ServiceList
              data={items}
              blockMode={blockMode}
              parentCategory={parentCategory}
            />
          </div>
        </section>
      )}
      {articleListType === "TILTING_CARD_WITH_IMAGE" && (
        <section class="site-block">
          <div class="wrapper">
            <AccountList
              data={items}
              blockMode={blockMode}
              parentCategory={parentCategory}
            />
          </div>
        </section>
      )}
      {articleListType === "PLAIN_CARD_2" && (
        <section class="site-block">
          <div class="wrapper">
            <FastLinks
              data={items.map((item) => {
                const url = buildURLForMenuItem(item).substring(1);
                const pureItem = item.article || item.category || item;

                return {
                  icon: "i-fibank-logo",
                  slug: url,
                  title: pureItem.title,
                };
              })}
            />
          </div>
        </section>
      )}
    </>
  );
};

export default function Category({ data }) {
  const { lang } = usePageContext();

  const category = data.api.category;
  const title = category.title;
  const subtitle = category.subtitle;
  const categories = category.parentCategory;
  const head = category.head || [];
  const body = category.body || [];
  const articles = category.articles || [];
  const categoryChildren = category.categories || [];
  const parentCategory = category.parentCategory || {};
  const showHeader = category.showHeader;
  const { articleListType } = category;

  // Editors control the order from the CMS via `displayOrder`. Entries without
  // one keep the previous behaviour: subcategories first, then articles by
  // most recently updated.
  const items = byDisplayOrder([...categoryChildren, ...articles]);
  return (
    <>
      <Helmet>
        <title>Fibank - {text(title, lang)}</title>
      </Helmet>
      <section>
        <div className="wrapper">
          <Breadcrumbs data={category} />
          <Patterns data={head} />
        </div>
      </section>

      {showHeader && <ArticleTitle data={{ title, subtitle }} />}
      <CategoryItems
        items={items}
        articleListType={articleListType}
        body={[]}
        parentCategory={data.api.category}
      />
      {body.length ? (
        <ArticleWrapper
          expanded={category.width === "WIDE" || category.width === "FULL"}
        >
          {category.width === "FULL" ? (
            <Patterns data={body} />
          ) : (
            <div className="wrapper">
              <Patterns data={body} />
            </div>
          )}
        </ArticleWrapper>
      ) : null}
    </>
  );
}

export const pageQuery = graphql`
  fragment Category on API_Categories {
    id
    slug
  }

  query($id: ID!) {
    api {
      category(id: $id, publicationState: LIVE) {
        displayTitle
        id
        slug
        width
        showHeader
        articleListType

        title {
          id
          text_en
          text_sq
        }

        subtitle {
          id
          text_en
          text_sq
        }

        categories {
          id
          displayOrder
          title {
            id
            text_sq
            text_en
          }
          subtitle {
            id
            text_sq
            text_en
          }
          excerpt {
            id
            text_sq
            text_en
          }
          cover {
            url
          }
          coverScrollingSlider {
            url
          }
          slug
          head {
            __typename
            ... on API_ComponentArticlePatternBodyFeatureIcons {
              id
              Icon {
                id
                feature {
                  id
                  text_sq
                  text_en
                }
                description {
                  id
                  text_sq
                  text_en
                }
                icon
              }
            }
          }
          parentCategory {
            ...Category
            slug
            title {
              id
              text_en
              text_sq
            }
            parentCategory {
              ...Category
              slug
              title {
                id
                text_en
                text_sq
              }
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                }
              }
            }
          }
        }

        articles(sort: "displayOrder:asc,updated_at:desc") {
          updated_at
          displayOrder
          id
          title {
            id
            text_sq
            text_en
          }
          slug
          excerpt {
            id
            text_sq
            text_en
          }
          subtitle {
            id
            text_sq
            text_en
          }
          cover {
            id
            formats
            url
          }
          coverScrollingSlider {
            url
          }

          head {
            __typename
            ... on API_ComponentArticlePatternBodyFeatureIcons {
              id
              Icon {
                id
                feature {
                  id
                  text_sq
                  text_en
                }
                description {
                  id
                  text_sq
                  text_en
                }
                icon
              }
            }
          }
        }

        parentCategory {
          ...Category
          slug
          title {
            id
            text_en
            text_sq
          }
          parentCategory {
            ...Category
            slug
            title {
              id
              text_en
              text_sq
            }
            parentCategory {
              ...Category
              slug
              title {
                id
                text_en
                text_sq
              }
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
              }
            }
          }
        }

        head {
          ...CategoriesHeadDynamicZoneFragment
        }

        body {
          ...CategoriesBodyDynamicZoneFragment

          ... on API_ComponentArticlePatternBodyTabs {
            id
            tabs {
              id
              title {
                id
                text_sq
                text_en
              }
              body {
                id
                text_sq
                text_en
              }
            }
          }

          ... on API_ComponentArticlePatternBodyReferencedTabs {
            id
            tabs {
              id
              tab {
                id
                __typename
                tabTitle {
                  text_en
                  text_sq
                }
                body {
                  ...ArticleTabsBodyDynamicZoneFragment
                }
              }
            }
          }

          ... on API_ComponentArticlePatternBodyFaq {
            id
            questions {
              id
              help_item {
                id
                created_at
                updated_at
                displayTitle
                title {
                  id
                  text_sq
                  text_en
                }
                content {
                  id
                  text_sq
                  text_en
                }
              }
            }
          }
        }
      }
    }
  }
`;
